import React, { useState, useEffect, useMemo, useRef  } from "react";
import { Row, Col, Form, Button, Input, Spinner } from "reactstrap";
import ComponentCard from "./ComponentCard";
import "../../index.css";
import { useTable, useSortBy, usePagination } from "react-table";
import "../../AppTable.css";
import DownloadDialog from "./DownloadDialog";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from "@mui/icons-material/Download";
import BuildIcon from '@mui/icons-material/Build';
import TrainAndEvalPlots from "./TrainAndEvalPlots";
import { parsePipelineConfigRich, buildConfigDisplay } from "./PipelineConfigParser";

import {
  IconButton,
  Tooltip,
  TableContainer,
  Table,
  Paper,
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  Box,
  Typography,
  CircularProgress,
  TextField,
  Alert
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import {
  stopEC2API,
  checkEC2StatusAPI,
} from "../../service/ModelTestingService";

import {
  fetchModelDataAPI,
  fetchDatasetsAPI,
  buildModelAPI,
  downloadModelAPI,
  getTrainEstimateAPI,
  fetchSubmittedBuildsAPI,
  startEC2API,
  deleteModelAPI,
  fetchModelConfigAPI,
  stopTrainingAPI,
  exportCheckpointAPI
} from "../../service/ModelManagementService";
import ArticleIcon from '@mui/icons-material/Article';

const authService = require("../../service/AuthService");

const ModelManagementForm = () => {
  const user = authService.getUser();
  const organization = user.organization.toLowerCase();
  const username = user.name.toLowerCase();

  const [modelData, setModelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datasetList, setDatasetList] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [popupData, setPopupData] = useState(null);
  const [progressLabel, setProgressLabel] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [chargesAccepted, setChargesAccepted] = useState(false);
  const [submittedBuilds, setSubmittedBuilds] = useState([]);
  const [modelSliderValue, setModelSliderValue] = useState(1); // 0 to 4
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configText, setConfigText] = useState("");
  const [configLoading, setConfigLoading] = useState(false);
  const [configModelName, setConfigModelName] = useState("");

  const [ec2Status, setEc2Status] = useState("");
  const [startingEC2, setStartingEC2] = useState(false);

  const [submitLabel, setSubmitLabel] = useState("Submit Build");
  const [statusMessage, setStatusMessage] = useState("");
  const [configParsed, setConfigParsed] = useState(null);
  const [configMetrics, setConfigMetrics] = useState(null); 
  const [configStepFolder, setConfigStepFolder] = useState("");
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportBuild, setExportBuild] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [stepText, setStepText] = useState("");
  const [stepError, setStepError] = useState("");
  const [lastCheckpointCount, setLastCheckpointCount] = useState(0);
  const [configDialogFull, setConfigDialogFull] = useState(false);
  const submittedBuildsRef = useRef(null);
  const [modelCfgOpen, setModelCfgOpen] = useState(false);
  const [modelCfgLoading, setModelCfgLoading] = useState(false);
  const [modelCfgName, setModelCfgName] = useState("");
  const [modelCfgStepLabel, setModelCfgStepLabel] = useState("");
  const [modelCfgTrainRows, setModelCfgTrainRows] = useState([]);
  const [modelCfgEvalRows, setModelCfgEvalRows] = useState([]);
  const [modelCfgError, setModelCfgError] = useState("");
  const [modelCfgNearestHint, setModelCfgNearestHint] = useState("");
  const [modelCfgStepAbs, setModelCfgStepAbs] = useState(null);

  const instanceId = "i-0e5494d7c4d042b3d";

  const [formData, setFormData] = useState({
    model_name: "",
    num_classes: "",
    batch_size: "",
    num_steps: "",
    early_stopping: false,
    training_start_point: "",
  });

  const ec2StateLower = String(ec2Status || "").toLowerCase();
  const ec2IsRunning = ec2StateLower === "running";

  const batchSizeOptions = [1, 2, 4, 8];
  const numStepsOptions = [
    1000,
    5000,
    10000,
    20000,
    50000,
    100000,
    300000,
    500000,
    800000,
    1000000,
  ];
  const trainingStartPointOptions = [
    "Beginning",
    "Continue from a checkpoint",
  ];
  const efficientDetModels = [
    { name: "EfficientDet-D0", ap: 34.6, flops: "2.54B" },
    { name: "EfficientDet-D1", ap: 40.5, flops: "6.10B" },
    { name: "EfficientDet-D1", ap: 40.5, flops: "6.10B" },
    { name: "EfficientDet-D2", ap: 43.0, flops: "11.0B" },
  ];

  // Max exportable step = number of checkpoints * 1000
  const maxExportStep = useMemo(
    () => (Number.isFinite(Number(lastCheckpointCount)) ? Number(lastCheckpointCount) * 1000 : 0),
    [lastCheckpointCount]
  );

  const CKPT_STRIDE = 1000;
  
  const parseStepLabelK = (label) => {
    const m = String(label || "").trim().match(/^(\d+)\s*K$/i);
    return m ? Number(m[1]) : null;
  };

  /** Pick {train, eval} for an absolute step (e.g., 3000) from metricsSeries */
  function selectFromSeries(metricsSeries, absStep, { nearest = true } = {}) {
    if (!metricsSeries || !Number.isFinite(absStep)) return null;

    const pickExact = (obj) => obj?.[String(absStep)] ?? obj?.[absStep] ?? null;

    const pickNearest = (obj) => {
      const keys = Object.keys(obj || {}).map(Number).filter(Number.isFinite);
      if (!keys.length) return { value: null, picked: null };
      let picked = keys[0], best = Math.abs(keys[0] - absStep);
      for (let i = 1; i < keys.length; i++) {
        const d = Math.abs(keys[i] - absStep);
        if (d < best) { best = d; picked = keys[i]; }
      }
      return { value: obj[String(picked)] ?? obj[picked] ?? null, picked };
    };

    // TRAIN
    let train = pickExact(metricsSeries.train);
    let pickedTrainStep = null;
    if (!train && nearest) {
      const r = pickNearest(metricsSeries.train);
      train = r.value; pickedTrainStep = r.picked;
    }

    // EVAL
    let evalm = pickExact(metricsSeries.eval);
    let pickedEvalStep = null;
    if (!evalm && nearest) {
      const r = pickNearest(metricsSeries.eval);
      evalm = r.value; pickedEvalStep = r.picked;
    }

    if (!train && !evalm) return null;
    return { step: absStep, train: train ?? null, eval: evalm ?? null, pickedTrainStep, pickedEvalStep };
  }


  // matches ".../<number>K[/pipeline.config]" → returns the number
  const extractCheckpoint = (p) => {
    const m = String(p || "").match(/\/(\d+)K(?:\/pipeline\.config)?\/?$/i);
    return m ? Number(m[1]) : null;
  };

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [buildDetailsOpen, setBuildDetailsOpen] = useState(false);
  const [buildDetails, setBuildDetails] = useState(null);

  const isValidModelName = (name) => {
    return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
  };

  const openBuildDetails = (build) => {
    setBuildDetails(build);
    setBuildDetailsOpen(true);
  };

  const closeBuildDetails = () => setBuildDetailsOpen(false);

  // Pretty labels for dialog rows
  const buildLabelMap = {
    dataset: "Dataset",
    num_classes: "Num Classes",
    data_size: "Data Size",
    num_steps: "No. of Steps",
    batch_size: "Batch Size",
    checkpoint: "Checkpoint",
    estimated_time: "Estimated Time",
    estimated_cost: "Estimated Cost",
    trainingStatus: "Training Status",
    exportStatus: "Export Status",
    exportStep: "Export Step",
    exportPID: "Export PID",
    exportLogPath: "Export Log",
    modelSaved: "Model Saved",
    modelS3Uri: "Model S3 URI",
    exportStartTime: "Export Start",
    exportEndTime: "Export End",
  };

  const dialogOmitKeys = new Set([
    "modelName",
    "startedTime",
    "status",
    "trainingStatus",      // shown as a separate column
    "user_name",
    "trainingProgressUrl", // shown as Progress URL
  ]);

  const prettyVal = (k, v) => {
    if (k === "estimated_cost") return formatUSD(v);
    if (v === 1 || v === "1") return "Completed";
    if (v === 0 || v === "0") return "Pending";
    return String(v ?? "");
  };


  const LEFT_ALIGN_ACCESSORS = new Set(["modelNameWithStep", "startedTime"]);
  const isLeftAlignedColumn = (col) => {
    const acc = col.id || col.accessor;
    return LEFT_ALIGN_ACCESSORS.has(acc);
  };

  const WRAP_STYLE = {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    minWidth: 300,       
  };

  // Pretty label for metric keys
  const prettyMetricLabel = (k) => {
    if (METRIC_LABELS[k]) return METRIC_LABELS[k];
    // Title-case "loss_loc" -> "Loss Loc"
    return String(k)
      .replace(/_/g, " ")
      .replace(/\b\w/g, (m) => m.toUpperCase());
  };

  // Turn an object {k:v} into [{label, value}]
  const kvToRows = (obj = {}) =>
    Object.entries(obj).map(([k, v]) => ({
      label: prettyMetricLabel(k),
      value:
        typeof v === "number" ? formatMetricValue(k, v)
        : typeof v === "object" ? JSON.stringify(v)
        : String(v),
    }));

  // Build TRAIN / EVAL row arrays out of a single-step metrics record
  function rowsFromSingleStepMetrics(m = {}) {
    // Prefer nested objects if present
    const train = (m.train && typeof m.train === "object") ? { ...m.train } : {};
    const evalm = (m.eval  && typeof m.eval  === "object") ? { ...m.eval  } : {};

    // If no nested objects, split by key prefixes train_* / eval_*
    if (!Object.keys(train).length && !Object.keys(evalm).length) {
      for (const [k, v] of Object.entries(m)) {
        if (k.startsWith("train_")) {
          train[k.replace(/^train_/, "")] = v;
        } else if (k.startsWith("eval_")) {
          evalm[k.replace(/^eval_/, "")] = v;
        }
      }
    }

    // Also surface per-IoU pairs (commonly in eval)
    const pairs = extractPerIouPairs(m);
    if (pairs.length) {
      for (const [thr, val] of pairs) {
        const key = `ap@${(thr * 100).toFixed(0)}`; // e.g., ap@50
        // don't override if already present
        if (evalm[key] == null) evalm[key] = val;
      }
    }

    // Remove obvious bookkeeping keys if they slipped in
    const strip = (o) => {
      delete o.step; delete o.global_step; delete o.training_step; delete o.iteration;
      return o;
    };

    return {
      trainRows: kvToRows(strip(train)),
      evalRows:  kvToRows(strip(evalm)),
    };
  }


  const buildKey = (b) => `${b.modelName}__${b.startedTime}`;

  const [exportingKeys, setExportingKeys] = useState(new Set());

  // Capitalize first letter and any letter after " " or "."
  const capName = (v) => {
    const s = (v ?? "").toString();
    if (!s) return "N/A";
    return s.replace(/(^|[ .])([a-z])/gi, (_, sep, ch) => sep + ch.toUpperCase());
  };

  // Normalize DynamoDB List/Array/String into a flat array of strings
  const normalizeTrainErrors = (value) => {
    if (!value) return [];
    // Already a plain array of strings
    if (Array.isArray(value)) {
      return value
        .flatMap((v) => normalizeTrainErrors(v))
        .map((s) => String(s))
        .filter(Boolean);
    }
    // DynamoDB attribute forms
    if (typeof value === "object" && value !== null) {
      if (Array.isArray(value.L)) {
        return value.L
          .map((x) => (x && typeof x === "object" && "S" in x ? x.S : String(x)))
          .filter(Boolean);
      }
      if ("S" in value) return [String(value.S)];
    }
    // Fallback
    return [String(value)];
  };

  const [configErrors, setConfigErrors] = useState([]);

  useEffect(() => {
    const initializePage = async () => {
      await fetchDatasets();
      await fetchModelData();
      await fetchSubmittedBuilds();
    };
    initializePage();
  }, []);

  useEffect(() => {
    checkEC2Status();
  }, []);

  const efficientDetBaseMap = {
    "EfficientDet-D0": "ssd_efficientdet_d0",
    "EfficientDet-D1": "ssd_efficientdet_d1",
    "EfficientDet-D1": "ssd_efficientdet_d1",
    "EfficientDet-D2": "ssd_efficientdet_d2",
  };

  useEffect(() => {
    if (!chargesAccepted) return;

    let timer;
    const refresh = async () => {
      const s = await checkEC2Status(); 
      if (/starting|stopping/i.test(s)) {
        // keep polling while it's moving between states
        timer = setTimeout(refresh, 5000);
      }
    };

    refresh();
    return () => timer && clearTimeout(timer);
  }, [chargesAccepted]);


  // Normalize  status helpers
  const norm = (v) => String(v || "").trim().toLowerCase();
  
  const isInProgress = (b) => {
    const ts = norm(b.trainingStatus);
    const s  = norm(b.status);
    return (
      ts === "inprogress" ||
      ts === "running"    ||
      ts === "launching"  ||
      s  === "in progress"||
      s  === "running"    ||
      s  === "launching"
    );
  };

  const isCompleted = (b) =>
    norm(b.trainingStatus) === "completed" || norm(b.status) === "completed";

  const readyToExport = (b) => {
    const x = norm(b.exportStatus || b.readyForExport);
    return x === "ready" || x === "1" || x === "true";
  };


  const isFailed = (b) =>
    norm(b.trainingStatus) === "failed" || norm(b.status) === "failed";


  const parseUSD = (v) => {
    if (v == null) return null;
    if (typeof v === "number") return v;
    // strip $, commas, spaces
    const n = Number(String(v).replace(/[$,\s]/g, ""));
    return Number.isFinite(n) ? n : null;
  };

  const formatUSD = (v) => {
    const n = parseUSD(v);
    if (n == null) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };


  const showProgressCol = useMemo(
    () => submittedBuilds.some((b) => isInProgress(b)),
    [submittedBuilds]
  );
  const fixedCols = 8; // Model, Started, Training Status, User, Data Prep, Model Performance, Status(Details), Action
  const totalCols = fixedCols + (showProgressCol ? 1 : 0);

  // Extract AP for IoU thresholds from various JSON shapes
  const extractPerIouPairs = (metrics = {}) => {
    const pairs = [];

    // If backend returns { per_iou: {"0.50":0.61, "0.75":0.43}, ... }
    if (metrics && typeof metrics.per_iou === "object" && metrics.per_iou !== null) {
      for (const [k, v] of Object.entries(metrics.per_iou)) {
        const thr = Number(k);
        if (Number.isFinite(thr) && typeof v === "number") pairs.push([thr, v]);
      }
    }

    // Also derive from flat keys like: map_50, ap@0.75, AP50, map50, etc.
    for (const [k, v] of Object.entries(metrics || {})) {
      if (typeof v !== "number") continue;
      const m = k.match(/(?:ap|map)[^0-9]*?(\d+(?:\.\d+)?)/i);
      if (!m) continue;
      let thr = Number(m[1]);
      if (!Number.isFinite(thr)) continue;
      if (thr > 1.0) thr = thr / 100; // support 50 => 0.50
      pairs.push([thr, v]);
    }

    // Dedup & sort by IoU
    const uniq = new Map();
    for (const [thr, val] of pairs) if (!uniq.has(thr)) uniq.set(thr, val);
    return Array.from(uniq.entries()).sort((a, b) => a[0] - b[0]);
  };

  // Compact list for table summary
  const SIMPLE_METRIC_KEYS = [
    "map", "map_50", "map_75",
    "ar_1", "ar_10", "ar_100",
    "loss_loc", "loss_cls", "loss_total",
  ];



  const checkEC2Status = async () => {
    try {
      const resp = await checkEC2StatusAPI(instanceId, false);
      console.log("checkEC2Status resp: ", resp);
      const s = (resp.ec2 || "unknown").toLowerCase();
      setEc2Status(s);
      return s;
    } catch (err) {
      console.error("Error fetching EC2 status", err);
      setEc2Status("error");
      return "error";
    }
  };

  const fetchModelData = async () => {
    try {
      const models = await fetchModelDataAPI(organization);
      console.log("fetchModelData resp: ", models);

      const enriched = (models || []).map(m => {
        const stepFromPath = extractCheckpoint(m.path);
        const stepK = m.stepK ?? stepFromPath ?? null;
        const stepLabel = stepK ? `${stepK}K` : "—";
        return { ...m, stepK, stepLabel };
      });

      setModelData(enriched);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching model data:", error);
      setLoading(false);
    }
  };

  const fetchDatasets = async () => {
    try {
      const datasets = await fetchDatasetsAPI(organization);
      console.log("fetchDatasets resp: ", datasets);
      setDatasetList(datasets);
    } catch (e) {
      console.error("Failed to fetch dataset list", e);
    }
  };

  const handleDatasetChange = (e) => {
    const selectedDataset = e.target.value;
    setSelectedDataset(selectedDataset);

    if (!selectedDataset) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        num_classes: "",
      }));
      return;
    }

    const selectedData = datasetList.find(
      (d) => d.folderName === selectedDataset
    );
    if (selectedData) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        num_classes: selectedData.num_classes?.toString() || "",
      }));
    }
  };

  const handleRebuildClick = (rowData) => {
    setSelectedDataset(rowData.dataset || "");
    setFormData({
      model_name: "",
      batch_size: rowData.batch_size || "",
      num_steps: rowData.num_steps || "",
      early_stopping: false,
      training_start_point: rowData.checkpoint || "",
      num_classes: rowData.num_classes?.toString() || "",
    });
  };

  const handleDownloadClick = async (rowData) => {
    setIsDownloading(true);
    setProgressLabel(`Starting download for model: ${rowData.modelName}`);

    const rawPath = String(rowData?.path || "");
    const nameWithStep = String(rowData?.modelNameWithStep || "");

    if (!rawPath || !nameWithStep) {
      setIsDownloading(false);
      setProgressLabel("");
      alert("Missing path or modelNameWithStep for this row.");
      return;
    }

    const safePath = rawPath.replace(/\/+$/, "");
    const modelPath = `${safePath}/${nameWithStep}`; 
    const zipModelPath = modelPath.endsWith(".zip") ? modelPath : `${modelPath}.zip`;

    console.log(zipModelPath);

    try {
      const { downloadUrl, fileName } = await downloadModelAPI(
        organization,
        zipModelPath
      );
      console.log(downloadUrl, fileName)
      setPopupData({ downloadUrl, fileName });
    } catch (err) {
      console.error("Error downloading model:", err?.response?.data || err.message);
      alert("Download failed.");
    } finally {
      setIsDownloading(false);
      setProgressLabel("");
    }
  };


  const extractMessage = (resp) => {
    try {
      if (typeof resp === "string") {
        const p = JSON.parse(resp);
        return p?.message ?? p?.body?.message ?? undefined;
      }
      if (resp && typeof resp === "object") {
        if (resp.body != null) {
          const payload =
            typeof resp.body === "string" ? JSON.parse(resp.body) : resp.body;
          return payload?.message;
        }
        return resp.message;
      }
    } catch (_) {}
    return undefined;
  };

  const handleConfigClick = async (model) => {
    if (isInProgress(model)) return;

    setConfigModelName(model?.modelName || "");
    setConfigLoading(true);
    setConfigDialogOpen(true);
    setConfigParsed(null);
    setConfigText("");
    setConfigMetrics(null);
    setConfigStepFolder("");
    setConfigDialogFull(false);

    const errs = normalizeTrainErrors(model?.trainPipelineError);
    setConfigErrors(errs);

    try {
      const resp = await fetchModelConfigAPI(organization, model.modelName, model.stepK);
      const raw = typeof resp.data === "string"
        ? resp.data
        : (resp.data.configText || resp.data.pipelineConfigText || "");

      setConfigText(raw);
      const sf = resp?.data?.stepFolder || "";
      setConfigStepFolder(sf);
      const metrics = resp?.data?.metrics || null;
      setConfigMetrics(metrics || null);

      const parsed = parsePipelineConfigRich(raw);
      const displayRows = buildConfigDisplay(parsed, sf);
      setConfigParsed(displayRows);
    } catch (e) {
      console.error("Failed to fetch config", e);
      setConfigText("Error loading config.");
    } finally {
      setConfigLoading(false);
    }
  };


  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const handleSubmitBuild = async () => {
    if (!isValidModelName(formData.model_name)) {
      alert("Please enter a valid model name. No spaces, special characters, or leading digits.");
      setIsSubmitting(false);
      return;
    }

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setSubmitLabel("Checking EC2 Status ....");
      setStatusMessage(""); 

      const selectedModel = efficientDetModels[modelSliderValue].name;
      const baseModelName = efficientDetBaseMap[selectedModel];

      const newBuild = {
        modelName: `${formData.model_name.trim()}_${Date.now()}`,
        preferredModel: baseModelName,
        startedTime: new Date().toLocaleString(),
        dataset: selectedDataset,
        num_classes: formData.num_classes || "N/A",
        data_size: "10 GB",
        num_steps: formData.num_steps || "N/A",
        batch_size: formData.batch_size || "N/A",
        estimated_time: estimate ? `${estimate.minutes} mins` : "N/A",
        estimated_cost: estimate != null ? Number(estimate.cost).toFixed(2) : "0.00",
        status: "In Progress",
        user_name: username,
        early_stopping: !!formData.early_stopping
      };

      console.log("newBuild: ", newBuild);

      // // 1) Check EC2 status
      // let status = await checkEC2Status();

      // if (status !== "running") {
      //   // Try to start it
      //   setStatusMessage("Waiting for EC2 to start.....");
      //   setSubmitLabel("Waiting for EC2 to start ....");
      //   try {
      //     const startResp = await startEC2API(instanceId);
      //     console.log("startec2Resp resp: ", startResp);
      //     const msg = (extractMessage(startResp) || status || "").toLowerCase();
      //     // If API returns immediate state, reflect it; otherwise keep polling
      //     if (msg) setEc2Status(msg);
      //   } catch (e) {
      //     console.error("Failed to start EC2:", e);
      //   }

      //   // Poll up to 3 times, 10s apart
      //   let attempts = 0;
      //   while (attempts < 3) {
      //     await sleep(10000);
      //     status = await checkEC2Status();
      //     if (status === "running") break;
      //     attempts++;
      //   }

      //   if (status !== "running") {
      //     setEc2Status(status);
      //     setStatusMessage(
      //       "EC2 could not be started, contact administrator to submit your training request."
      //     );
      //     setSubmitLabel("Submit Build");
      //     setIsSubmitting(false);
      //     return;
      //   }
      // }

      // // 2) If we are here, EC2 is running. Trigger pipeline.
      // setEc2Status("running");
      // setStatusMessage(
      //   "EC2 started successfully. Submitting build request....."
      // );
      // setSubmitLabel("Submitting Build ...");

      setStatusMessage(
        "Submitting build request....."
      );
      setSubmitLabel("Submitting Build ...");

      try {
        const resp= await buildModelAPI(organization, username, newBuild);
        console.log("buildModelAPI resp: ", resp);
        alert("Build submitted successfully!");
        // Refresh the Submitted Builds table
        await fetchSubmittedBuilds();

        if (submittedBuildsRef.current) {
          submittedBuildsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          submittedBuildsRef.current.focus({ preventScroll: true });
        }

        // Reset build form
        setSelectedDataset("");
        setFormData({
          model_name: "",
          num_classes: "",
          batch_size: "",
          num_steps: "",
          early_stopping: false,
          training_start_point: "",
        });
        setChargesAccepted(false);
        setEstimate(null);
        setModelSliderValue(1);
        setStatusMessage(""); // clear after success
      } catch (err) {
        console.error("Error building model:", err?.response?.data || err.message);
        setStatusMessage(
          `Failed to submit build: ${err?.response?.data?.message || err.message || "Unknown error"}`
        );
      }
    } finally {
      setSubmitLabel("Submit Build");
      setIsSubmitting(false);
    }
  };

  const getTrainingEstimate = async () => {
    try {
      const estimateResponse = await getTrainEstimateAPI(
        organization,
        formData.num_classes,
        formData.batch_size,
        formData.num_steps,
        formData.training_start_point
      );
      setEstimate({
        minutes: estimateResponse.message.estimatedTrainingTimeMinutes,
        cost: parseUSD(estimateResponse.message.estimatedCostUSD),
      });
      setChargesAccepted(false);
    } catch (err) {
      console.error("Error getting training estimate:", err?.response?.data || err.message);
    }
  };

  const fetchSubmittedBuilds = async () => {
    try {
      const builds = await fetchSubmittedBuildsAPI(organization);
      console.log("Submitted builds: ", builds);
      setSubmittedBuilds(builds);
    } catch (err) {
      console.error("Error fetching submitted builds:", err);
    }
  };

  const data = useMemo(() => modelData, [modelData]);
  const columns = useMemo(
    () => [
      { Header: "Model Name", accessor: "modelNameWithStep" },
      {
        Header: "Step",
        accessor: "stepLabel",
        sortType: (rowA, rowB) => {
          const a = rowA.original.stepK ?? -1;
          const b = rowB.original.stepK ?? -1;
          return a - b;
        },
        Cell: ({ value }) => value || "—",
      },
      { Header: "Build Date",
        accessor: "startedTime",
        Cell: ({ value }) => {
          const d = new Date(value);
          if (!value || isNaN(d.getTime())) {
            return "8/28/2025, 00:00:00 PM	";
          }
          return d.toLocaleString();
        },
      },
      { Header: "Classes", accessor: "num_classes" },
      { Header: "Batch Size", accessor: "batch_size" },
      { Header: "Steps", accessor: "num_steps" },
      { Header: "User Name",
        accessor: "user_name",
        Cell: ({ value }) => <span>{capName(value)}</span>,
      },
      { Header: "Config",
        id: "config",
        Cell: ({ row }) => (
          <IconButton
            size="small"
            color="info"
            style={{ width: 26, height: 26, padding: 2 }}
            onClick={() => handleModelConfigClick(row.original)}
          >
            <Tooltip title="View Config">
              <ArticleIcon fontSize="small" />
            </Tooltip>
          </IconButton>
        ),
      },
      {
        Header: "Rebuild",
        id: "rebuild",
        Cell: ({ row }) => (
          <Tooltip title="Rebuild">
            <IconButton
              size="small"
              color="primary"
              aria-label="rebuild"
              onClick={(e) => {
                e.preventDefault();
                handleRebuildClick(row.original);
              }}
              style={{ width: 28, height: 28, padding: 2 }}
            >
              <BuildIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        Header: "Download",
        id: "download",
        Cell: ({ row }) => (
          <Tooltip title="Download Model">
            <IconButton
              color="primary"
              size="small"
              style={{ width: 28, height: 28, padding: 2 }}
              onClick={(e) => {
                e.preventDefault();
                handleDownloadClick(row.original);
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
      {
        Header: "Delete",
        id: "delete",
        Cell: ({ row }) => (
          <Tooltip title="Delete Model">
            <IconButton
              size="small"
              color="error"
              style={{ width: 26, height: 26, padding: 2 }}
              onClick={() => { setModelToDelete(row.original); setDeleteDialogOpen(true); }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    []
  );

  const handleRefreshData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchModelData(), fetchSubmittedBuilds()]);
    } catch (err) {
      console.error("Refresh failed:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleStopEC2 = async () => {
    try {
      await stopEC2API(instanceId);
    } catch (error) {
      console.error("Error stopping EC2:", error);
    }
    await checkEC2Status();
  };

  const handleStartEC2 = async () => {
    setStartingEC2(true);
    setEc2Status("starting");
    try {
      await startEC2API(instanceId);
    } catch (error) {
      console.error("Error starting EC2:", error);
    } finally {
      await checkEC2Status();
      setStartingEC2(false);
    }
  };

  const handleDeleteModel = async () => {
    if (!modelToDelete) return;
    console.log(modelToDelete);
    try {
      await deleteModelAPI(organization, username, modelToDelete.modelName);
      setDeleteDialogOpen(false);
      await fetchModelData();
    } catch (err) {
      console.error("Error deleting model:", err);
      alert("Failed to delete model.");
    }
  };


  const tableInstance = useTable({ columns, data }, useSortBy, usePagination);
  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } =
    tableInstance;

  const handleStopTraining = async (build) => {
    const confirm = window.confirm(`Stop training for "${build.modelName}"?`);
    if (!confirm) return;

    try {
      await stopTrainingAPI(organization, build.modelName);
      alert("Stop command sent. Training will be terminated shortly.");
      await fetchSubmittedBuilds();
    } catch (err) {
      console.error("Stop training failed:", err?.response?.data || err.message);
      alert(`Failed to stop training: ${err?.response?.data?.message || err.message}`);
    }
  };


  const openExportDialog = (build) => {
    setExportBuild(build);
    setExportDialogOpen(true);

    const raw = build.lastCheckpointStep ?? build.lastcheckpointstep ?? 0;
    const count = Number(raw) || 0;

    setLastCheckpointCount(count);

    if (count > 0) {
      const prefill = count * CKPT_STRIDE;
      setStepText(String(prefill));       // prefill with the latest valid step
      setStepError("");
    } else {
      setStepText("");
      setStepError("No checkpoints found yet.");
    }
  };


  const handleExportModel = async () => {
    if (!exportBuild) return;
    const n = Number(String(stepText).trim());

    if (!Number.isFinite(n) || n < 1) {
      alert("Please enter a numeric step ≥ 1."); return;
    }
    if (n % CKPT_STRIDE !== 0) {
      alert(`Step must be a multiple of ${CKPT_STRIDE}.`); return;
    }
    if (n > maxExportStep) {
      alert(`Step cannot exceed ${maxExportStep}.`); return;
    }

    setExporting(true);
    try {
      const resp = await exportCheckpointAPI(
        organization,
        username,
        exportBuild.dataset,
        exportBuild.modelName,
        n,
        exportBuild.startedTime
      );
      alert(`Export queued:\nModel: ${exportBuild.modelName}\nStep: ${n}`);

      setExportingKeys(prev => {
        const next = new Set(prev);
        next.add(buildKey(exportBuild));
        return next;
      });
      await fetchSubmittedBuilds();
      setExportDialogOpen(false);
    } catch (err) {
      // ...
    } finally {
      setExporting(false);
    }
  };


  const METRIC_LABELS = {
    map: "mAP @[.50:.95]",
    map_50: "mAP@0.50",
    map_75: "mAP@0.75",
    map_small: "mAP (Small)",
    map_medium: "mAP (Medium)",
    map_large: "mAP (Large)",
    ar_1: "AR@1",
    ar_10: "AR@10",
    ar_100: "AR@100",
    loss_loc: "Loc Loss",
    loss_cls: "Cls Loss",
    loss_total: "Total Loss",
  };

  const METRIC_ORDER = [
    "map", "map_50", "map_75", "map_small", "map_medium", "map_large",
    "ar_1", "ar_10", "ar_100",
    "loss_loc", "loss_cls", "loss_total",
  ];

  const formatMetricValue = (k, v) => {
    if (v == null) return "N/A";
    if (typeof v !== "number") return String(v);
    if (v < 0) return "N/A"; // for -1.0 (e.g., map_small)
    // Percent-style for mAP/AR
    if (k.startsWith("map") || k.startsWith("ar_")) {
      return `${(v * 100).toFixed(1)}%`;
    }
    // Losses
    return v.toFixed(3);
  };

  // use a function declaration so it's hoisted
  function isExportingNow(b) {
    const s = String(b.exportStatus || b.exportstatus || "").toLowerCase();
    if (/^(completed|done|failed|error|stopped|canceled|cancelled)$/.test(s)) return false;
    const backendExporting =
      /^(exporting|running|inprogress|queued|pending|starting|uploading|syncing)$/i.test(s);
    return backendExporting || exportingKeys.has(buildKey(b));
  }

  const handleModelConfigClick = async (model) => {
    setModelCfgOpen(true);
    setModelCfgLoading(true);
    setModelCfgName(model?.modelName || "");
    setModelCfgStepLabel(model?.stepLabel || "");
    setModelCfgError("");
    setModelCfgTrainRows([]);
    setModelCfgEvalRows([]);
    setModelCfgNearestHint("");
    setModelCfgStepAbs(null);

    try {
      // Resolve K from row (prefer numeric stepK; fallback to parsing "3K")
      const kFromRow =
        (typeof model.stepK === "number" ? model.stepK : null) ??
        parseStepLabelK(model.stepLabel);

      const resp = await fetchModelConfigAPI(organization, model.modelName, kFromRow);

      // Full time series from backend
      const series = resp?.data?.metricsSeries || null;

      // Absolute step requested (e.g., 3K -> 3000)
      const absStep = Number.isFinite(kFromRow) ? kFromRow * 1000 : null;
      setModelCfgStepAbs(absStep);

      // Slice to a single step (with nearest fallback)
      const single = selectFromSeries(series, absStep, { nearest: true });

      // Build a small '(nearest: ...)' hint if backend had to pick adjacent steps
      if (single) {
        const parts = [];
        if (Number.isFinite(single.pickedTrainStep) && single.pickedTrainStep !== absStep) {
          parts.push(`train@${single.pickedTrainStep}`);
        }
        if (Number.isFinite(single.pickedEvalStep) && single.pickedEvalStep !== absStep) {
          parts.push(`eval@${single.pickedEvalStep}`);
        }
        setModelCfgNearestHint(parts.length ? `(nearest: ${parts.join(", ")})` : "");
      } else {
        setModelCfgNearestHint("");
      }

      // Populate tables
      if (!single) {
        setModelCfgError("No metrics found for this step.");
      } else {
        const { trainRows, evalRows } = rowsFromSingleStepMetrics(single);
        setModelCfgTrainRows(trainRows);
        setModelCfgEvalRows(evalRows);
        if (!trainRows.length && !evalRows.length) {
          setModelCfgError("No train/eval metrics available for this step.");
        }
      }
    } catch (err) {
      console.error("Failed to load metrics", err);
      setModelCfgError("Error loading metrics.");
    } finally {
      setModelCfgLoading(false);
    }
  };


  return (
    <div align="center" style={{ paddingTop: "0px" }}>
      <Row
        className="justify-content-center"
        style={{ margin: "0 auto", maxWidth: "1200px" }}
      >
        <Col sm="12" md="12">
          {/* Section 1: Model Summary */}
          <ComponentCard>
            {isDownloading && progressLabel && (
              <Row style={{ paddingTop: "0px", marginBottom: "20px" }}>
                <div style={{ color: "green" }}>{progressLabel}</div>
              </Row>
            )}

            <Row className="justify-content-center">
              <Col md="12">
                <h5 style={{ fontWeight: "bold", marginBottom: "20px" }}>
                  Trained Models' Summary
                </h5>
                <Row
                  className="justify-content-end"
                  style={{ marginBottom: "10px", paddingRight: "20px" }}
                >
                  <Col md="auto">
                    <Tooltip title="Refresh Table Data">
                      <span>
                        <IconButton onClick={handleRefreshData} color="default">
                          <RefreshIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Col>
                </Row>
                {loading ? (
                  <Spinner color="primary" />
                ) : (
                  <div className="app-table-wrapper">
                    <table {...getTableProps()} className="app-table">
                      <thead>
                        {headerGroups.map((headerGroup) => {
                          const headerGroupProps = headerGroup.getHeaderGroupProps();
                          const { key, ...restHeaderGroupProps } = headerGroupProps;
                          return (
                            <tr key={key} {...restHeaderGroupProps}>
                              {headerGroup.headers.map((column) => {
                                const columnProps = column.getHeaderProps(column.getSortByToggleProps());
                                const { key: columnKey, ...restColumnProps } = columnProps;
                                return (
                                  <th
                                    key={columnKey}
                                    {...restColumnProps}
                                    style={{ textAlign: "center" }}   
                                  >
                                    {column.render("Header")}
                                    <span>
                                      {column.isSorted ? (column.isSortedDesc ? " 🔽" : " 🔼") : ""}
                                    </span>
                                  </th>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </thead>

                      <tbody {...getTableBodyProps()}>
                        {page.map((row) => {
                          prepareRow(row);
                          const rowProps = row.getRowProps();
                          const { key: rowKey, ...restRowProps } = rowProps;
                          return (
                            <tr key={rowKey} {...restRowProps}>
                              {row.cells.map((cell) => {
                                const cellProps = cell.getCellProps();
                                const { key: cellKey, ...restCellProps } = cellProps;
                                return (
                                  <td
                                    key={cellKey}
                                    {...restCellProps}
                                    style={{
                                      textAlign: isLeftAlignedColumn(cell.column) ? "left" : "center",
                                    }}
                                  >
                                    {cell.render("Cell")}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                  </div>
                )}
              </Col>
            </Row>
          </ComponentCard>

          <ComponentCard>
            <Row className="justify-content-center">
              <Col md="12">
                <h5 ref={submittedBuildsRef} tabIndex={-1} style={{ fontWeight: "bold", marginBottom: "20px" }}>
                  Submitted Builds
                </h5>
                <Row
                  className="justify-content-end"
                  style={{ marginBottom: "10px", paddingRight: "20px" }}
                >
                  <Col md="auto">
                    <Tooltip title="Refresh Table Data">
                      <span>
                        <IconButton onClick={handleRefreshData} color="default">
                          <RefreshIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Col>
                </Row>

                {/* Submitted Builds */}
                {loading ? (
                  <Spinner color="primary" />
                  ) : (
                    <div className="app-table-wrapper" >
                      <table className="app-table" >
                        <thead >
                          <tr>
                            <th style={{ textAlign: "center" }}>Model Name</th>
                            <th style={{ textAlign: "center" }}>Started Time</th>
                            <th style={{ textAlign: "center" }}>Training Status</th>
                            <th style={{ textAlign: "center" }}>User Name</th>
                            {showProgressCol && <th style={{ textAlign: "center" }}>Progress URL</th>}
                            <th style={{ textAlign: "center" }}>Data Prep</th>
                            <th style={{ textAlign: "center" }}>Training Details</th>
                            <th style={{ textAlign: "center" }}>Status</th>
                            <th style={{ textAlign: "center" }}>Action</th>
                          </tr>
                        </thead>
                        <tbody style={{ textAlign: "center" }}>
                          {submittedBuilds.length === 0 ? (
                            <tr>
                              <td colSpan={totalCols} style={{ textAlign: "center", padding: "16px", color: "#6c757d" }}>
                                No build is in progress.
                              </td>
                            </tr>
                          ) : (
                            submittedBuilds.map((build, index) => {
                              const progressCell =
                                isInProgress(build)
                                  ? (build.trainingProgressUrl
                                      ? <a href={build.trainingProgressUrl} target="_blank" rel="noopener noreferrer">Click Here</a>
                                      : <span style={WRAP_STYLE}>Link will be available once the training starts</span>)
                                  : null;

                              // data prep progress
                              const prepSteps = [
                                "dataPrepStatus",
                                "labelMapStatus",
                                "dataSplitStatus",
                                "xmlToCsvStatus",
                                "tfRecsStatus",
                                "modelConfigStatus",
                              ];
                              let completedSteps = 0;
                              for (let i = 0; i < prepSteps.length; i++) {
                                if (build[prepSteps[i]] === "1" || build[prepSteps[i]] === 1) {
                                  completedSteps++;
                                } else {
                                  break;
                                }
                              }
                              const prepProgress = `${completedSteps}/6 completed`;
                              const trainingDone = isCompleted(build);
                              const exportingNow = isExportingNow(build); 

                              let extraAction = null;

                              if (trainingDone && exportingNow) {
                                extraAction = <Button size="sm" disabled>Exporting...</Button>;
                              } else if (!isFailed(build) && isInProgress(build)) {
                                extraAction = (
                                  <Tooltip title="Stop Training">
                                    <IconButton color="error" size="small" onClick={() => handleStopTraining(build)}>
                                      <StopIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                );
                              } else if (trainingDone) {
                                // training completed -> ALWAYS allow export (regardless of exportStatus)
                                extraAction = (
                                  <Button size="sm" onClick={() => openExportDialog(build)}>
                                    Save &amp; Export
                                  </Button>
                                );
                              }
                              // -------------------------------------


                              return (
                                <tr key={index}>
                                  <td>{build.modelName}</td>
                                  <td>{build.startedTime}</td>
                                  <td>{capName(build.trainingStatus) || "N/A"}</td>
                                  <td>{capName(build.user_name)}</td>

                                  {showProgressCol && (
                                    <td style={{ textAlign: "center" }}>
                                      {progressCell}
                                    </td>
                                  )}

                                  <td>{prepProgress}</td>
                                  <td style={{ textAlign: "center" }}>
                                    <Tooltip
                                      title={
                                        isInProgress(build)
                                          ? "Unavailable while training is in progress"
                                          : isFailed(build)
                                          ? "View Error"
                                          : isCompleted(build)
                                          ? "View Performance"
                                          : "View Details"
                                      }
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="info"
                                          style={{ width: 26, height: 26, padding: 2 }}
                                          disabled={isInProgress(build)}
                                          onClick={() => {
                                            if (!isInProgress(build)) handleConfigClick(build);
                                          }}
                                        >
                                          <ArticleIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    <Tooltip title="Details">
                                      <IconButton
                                        size="small"
                                        color="info"
                                        style={{ width: 26, height: 26, padding: 2 }}
                                        onClick={() => openBuildDetails(build)}
                                      >
                                        <ArticleIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    {extraAction}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>

                      </table>
                    </div>
                  )}

              </Col>
            </Row>
          </ComponentCard>


          {/* Section 2: Build New Model */}
          <ComponentCard>
            <h5 style={{ fontWeight: "bold", marginBottom: "20px" }}>
              Build a New Model
            </h5>
            <div
              style={{
                maxWidth: "1000px",
                margin: "0 auto",
                marginTop: "10px",
                marginBottom: "0px",
              }}
            >
              <div
                style={{
                  marginTop: "20px",
                  backgroundColor: "#f9f9f9",
                  borderRadius: "8px",
                  padding: "20px",
                }}
              >
                <Form>
                  <Row
                    className="justify-content-center"
                    style={{ marginBottom: "30px" }}
                  >
                    <Col md="5">
                        <label
                          style={{
                            fontWeight: "bold",
                            fontSize: "13px",
                            marginBottom: "10px",
                          }}
                        >
                          Model Name
                        </label>
                        <Input
                          type="text"
                          value={formData.model_name}
                          placeholder="Example, my_model"
                          onChange={(e) =>
                            setFormData({ ...formData, model_name: e.target.value })
                          }
                          style={{ height: "35px", fontSize: "13px" }}
                        />
                        {!isValidModelName(formData.model_name) && formData.model_name.length > 0 && (
                          <div style={{ fontSize: "12px", color: "red", marginTop: "4px" }}>
                            Invalid model name. Use letters, digits, or underscores. Must start with a letter or underscore.
                          </div>
                        )}
                      </Col>
                    <Col md="5">
                      <label
                        style={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                      >
                        Dataset
                      </label>
                      <Input
                        type="select"
                        value={selectedDataset}
                        onChange={handleDatasetChange}
                        style={{ height: "35px", fontSize: "13px" }}
                      >
                        <option value="">-- Select --</option>
                        {datasetList.map((dataset, idx) => (
                          <option key={idx} value={dataset.folderName}>
                            {dataset.folderName} - {dataset.updatedttm}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <Row style={{ marginBottom: "20px" }}>
                    <Col md="3">
                      <label
                        style={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                      >
                        Number of Classes
                      </label>
                      <Input
                        id="numClasses"
                        type="text"
                        value={formData.num_classes}
                        disabled
                        style={{
                          height: "30px",
                          fontSize: "13px",
                          backgroundColor: "#e9ecef",
                          cursor: "not-allowed",
                        }}
                      />
                    </Col>

                    <Col md="3">
                      <label
                        style={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                      >
                        Batch Size
                      </label>
                      <Input
                        id="batchSize"
                        type="select"
                        value={formData.batch_size}
                        onChange={(e) =>
                          setFormData({ ...formData, batch_size: e.target.value })
                        }
                        style={{ height: "30px", fontSize: "13px" }}
                      >
                        <option value="">-- Select --</option>
                        {batchSizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </Input>
                    </Col>

                    <Col md="3">
                      <label
                        style={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                      >
                        Number of Steps
                      </label>
                      <Input
                        id="numIterations"
                        type="select"
                        value={formData.num_steps}
                        onChange={(e) =>
                          setFormData({ ...formData, num_steps: e.target.value })
                        }
                        style={{ height: "30px", fontSize: "13px" }}
                      >
                        <option value="">-- Select --</option>
                        {numStepsOptions.map((step) => (
                          <option key={step} value={step}>
                            {step.toLocaleString()}
                          </option>
                        ))}
                      </Input>
                    </Col>

                    <Col md="3">
                      <label
                        style={{
                          fontWeight: "bold",
                          fontSize: "13px",
                          marginBottom: "10px",
                        }}
                      >
                        Training Start Point
                      </label>
                      <Input
                        id="trainingStartPoint"
                        type="select"
                        value={formData.training_start_point}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            training_start_point: e.target.value,
                          })
                        }
                        style={{ height: "30px", fontSize: "13px" }}
                      >
                        <option value="">-- Select --</option>
                        {trainingStartPointOptions.map((step) => (
                          <option key={step} value={step}>
                            {step.toLocaleString()}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  </Row>

                  <Row style={{ marginTop: "10px" }}>
                    <div
                      style={{
                        margin: "30px auto",
                        maxWidth: "600px",
                        textAlign: "center",
                      }}
                    >
                      <h6 style={{ fontWeight: "bold" }}>
                        Model Speed vs Accuracy
                      </h6>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "14px",
                        }}
                      >
                        <span style={{ marginRight: "10px", marginBottom: "4px" }}>
                          Speed
                        </span>
                        <input
                          type="range"
                          min="0"
                          max="4"
                          value={modelSliderValue}
                          onChange={(e) =>
                            setModelSliderValue(Number(e.target.value))
                          }
                          style={{ width: "100%" }}
                        />
                        <span style={{ marginLeft: "10px", marginBottom: "4px" }}>
                          Accuracy
                        </span>
                      </div>
                    </div>
                  </Row>
                <Row style={{ marginTop: "20px" }}> 
                  <Col md="12" align="center"> 
                    <label style={{ fontWeight: "bold", fontSize: "14px", fontWeight: "bold", fontSize: "13px", marginBottom: "10px" }} > 
                    <Input 
                        type="checkbox" 
                        checked={formData.early_stopping} 
                        onChange={(e) => setFormData({ ...formData, early_stopping: e.target.checked })} style={{ marginRight: "10px" }} /> 
                      Enable Early Stopping 
                    </label> 
                    </Col> 
                </Row>
                  <Row style={{ marginTop: "10px" }}>
                    <Col md="12">
                      <Button
                        style={{
                          backgroundColor: "#eaf9ea",
                          color: "grey",
                          marginRight: "20px",
                          fontSize: "14px",
                          marginTop: "10px",
                        }}
                        onClick={getTrainingEstimate}
                      >
                        Estimate Training Cost
                      </Button>
                    </Col>
                  </Row>

                  {estimate && (
                    <Row style={{ marginTop: "20px" }}>
                      <Col md="12">
                        <div
                          style={{
                            backgroundColor: "#f9f9f9",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            padding: "15px",
                            marginTop: "20px",
                            maxWidth: "300px",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "normal",
                              fontSize: "14px",
                              marginBottom: "10px",
                            }}
                          >
                            Estimated Training Time:&nbsp;
                            <span style={{ fontWeight: "bold", color: "green" }}>
                              {estimate.minutes} minutes
                            </span>
                            <br />
                            Estimated Cost:&nbsp;
                            <span style={{ fontWeight: "bold", color: "green" }}>
                              {formatUSD(estimate.cost)}
                            </span>
                          </div>
                          <div>
                            <label style={{ fontWeight: "bold", fontSize: "14px" }}>
                              <Input
                                type="checkbox"
                                checked={chargesAccepted}
                                style={{ marginRight: "10px" }}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  setChargesAccepted(isChecked);
                                  if (!isChecked) {
                                    setFormData((prev) => ({
                                      ...prev,
                                      early_stopping: false,
                                    }));
                                  }
                                }}
                              />
                              Accept charges and proceed
                            </label>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  )}

                  {chargesAccepted && (
                    <>
                      {/* <TableContainer
                        component={Paper}
                        sx={{
                          maxWidth: 300,
                          margin: "auto",
                          marginTop: "20px",
                          marginBottom: "8px",
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell align="center">
                                <strong>EC2 Status</strong>
                              </TableCell>
                              <TableCell align="center">
                                <strong>Actions</strong>
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell align="center">
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  gap={1}
                                >
                                  <Tooltip
                                    title={ec2IsRunning ? "Stop VM" : "Start VM"}
                                  >
                                    <span>
                                      <IconButton
                                        onClick={
                                          ec2IsRunning ? handleStopEC2 : handleStartEC2
                                        }
                                        disabled={/starting|stopping/.test(ec2Status)}
                                        color={ec2IsRunning ? "error" : "primary"}
                                      >
                                        {ec2IsRunning ? (
                                          <StopIcon />
                                        ) : (
                                          <PlayArrowIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color:
                                        ec2Status === "running"
                                          ? "green"
                                          : ec2Status === "stopped"
                                          ? "gray"
                                          : /starting/i.test(ec2Status) ||
                                            /stopping/i.test(ec2Status)
                                          ? "orange"
                                          : "red",
                                      fontWeight: "bold",
                                      textTransform: "capitalize",
                                    }}
                                  >
                                    {ec2Status || "Loading..."}
                                  </Typography>

                                  {(/starting/i.test(ec2Status) ||
                                    /stopping/i.test(ec2Status)) && (
                                    <CircularProgress size={16} color="warning" />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="Check VM Status">
                                  <IconButton onClick={checkEC2Status} color="default">
                                    <RefreshIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer> */}

                      {/* live status message */}
                      {statusMessage && (
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: "center",
                            color:
                              /could not be started/i.test(statusMessage) ||
                              /failed/i.test(statusMessage)
                                ? "red"
                                : "black",
                            mb: 2,
                            mt: 3
                          }}
                        >
                          {statusMessage}
                        </Typography>
                      )}
                    </>
                  )}

                  <Row hidden={!chargesAccepted} style={{marginTop:"50px"}}>
                    <Col md="12" align="center">
                      <Button
                        style={{
                          backgroundColor: "#eaf9ea",
                          color: "grey",
                          fontSize: "14px",
                        }}
                        onClick={handleSubmitBuild}
                        disabled={!chargesAccepted || isSubmitting}
                        aria-busy={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner
                              size="sm"
                              color="secondary"
                              style={{ marginRight: 8 }}
                            />
                            {submitLabel}
                          </>
                        ) : (
                          submitLabel
                        )}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </div>
            </div>
          </ComponentCard>

          {/* Download Dialog */}
          <DownloadDialog
            open={Boolean(popupData)}
            onClose={() => setPopupData(null)}
            downloadUrl={popupData?.downloadUrl}
            fileName={popupData?.fileName}
          />

          <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
            <DialogTitle>Confirm delete</DialogTitle>
            <DialogContent>
              Are you sure you want to delete model "{modelToDelete?.modelName}"?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button color="error" onClick={handleDeleteModel}>Delete</Button>
            </DialogActions>
          </Dialog>

          {/* === Config Dialog (metrics table) === */}
          <Dialog
            open={configDialogOpen}
            onClose={() => setConfigDialogOpen(false)}
            fullWidth
            fullScreen={configDialogFull}
            maxWidth={configDialogFull ? "xl" : "md"}
          >
            <DialogTitle sx={{ textAlign: "center" }}>
              {`${configModelName || "Model"}`}
            </DialogTitle>

            <DialogContent dividers>
              {configLoading ? (
                <Box display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {configErrors.length > 0 && (
                    <>
                      <Typography
                        align="center"
                        sx={{ mb: 1, fontSize: "18px", color: "error.main" }}
                      >
                        Training Error
                      </Typography>
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          textAlign: "left",
                        }}
                      >
                        {configErrors.join("\n")}
                      </Alert>
                    </>
                  )}

                  {/* ===== Evaluation Metrics ===== */}
                  <Typography align="center" sx={{ mb: 1, fontSize: "18px" }}>
                    Evaluation Metrics
                  </Typography>

                  <TrainAndEvalPlots metrics={configMetrics} />

                  {/* ===== Training Parameters (from pipeline.config) ===== */}
                  <Typography align="center" sx={{ mt: 5, mb: 1, fontSize: "18px" }}>
                    Training Parameters
                  </Typography>

                  {Array.isArray(configParsed) && configParsed.length > 0 ? (
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{
                        borderLeft: 4,
                        borderColor: "divider",
                        backgroundColor: (t) =>
                          t.palette.mode === "light"
                            ? t.palette.background.paper
                            : "rgba(255,255,255,0.02)",
                      }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Parameter</strong></TableCell>
                            <TableCell><strong>Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {configParsed.map((row, idx) => {
                            // Support either [{label,value}] or [["Label","Value"]] shapes
                            const label = row?.label ?? row?.name ?? row?.key ?? row?.[0] ?? "";
                            const value = row?.value ?? row?.[1] ?? "";
                            return (
                              <TableRow key={idx}>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>{String(label)}</TableCell>
                                <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  {typeof value === "object" ? JSON.stringify(value) : String(value)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box
                      component="pre"
                      sx={{
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        p: 2,
                        m: 0,
                        borderRadius: 1,
                        borderLeft: 4,
                        borderColor: "divider",
                        borderStyle: "solid",
                        borderWidth: 1,
                        backgroundColor: (t) =>
                          t.palette.mode === "light"
                            ? t.palette.grey[50]
                            : "rgba(255,255,255,0.04)",
                      }}
                    >
                      {configText}
                    </Box>
                  )}

                </>
              )}
            </DialogContent>


            <DialogActions>
              <Button size="small" onClick={() => setConfigDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={exportDialogOpen} onClose={()=>setExportDialogOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Export & Save Model</DialogTitle>
            <DialogContent dividers>
              {lastCheckpointCount <= 0 ? (
                <Typography variant="body2">
                  No checkpoints found yet. Try again later.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Enter the checkpoint <strong>step</strong> to export. Only numbers allowed.
                    <br />
                    <em>Max allowed:</em> <strong>{maxExportStep}</strong> ({lastCheckpointCount} checkpoints × 1000)
                  </Typography>

                  <TextField
                    label="Checkpoint step"
                    value={stepText}
                    disabled={exporting}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setStepText(v);

                      if (!v) return setStepError("Enter a step.");

                      const n = Number(v);
                      if (!Number.isFinite(n))        return setStepError("Step must be a number.");
                      if (n < 1)                      return setStepError("Step must be ≥ 1.");
                      if (n % CKPT_STRIDE !== 0)      return setStepError(`Step must be a multiple of ${CKPT_STRIDE}.`);
                      if (n > maxExportStep)          return setStepError(`Step cannot exceed ${maxExportStep}.`);

                      setStepError("");
                    }}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                    }}
                    type="text"
                    inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                    fullWidth
                    error={Boolean(stepError)}
                    helperText={stepError || `Max allowed: ${maxExportStep} (stride ${CKPT_STRIDE})`}
                  />

                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={()=>setExportDialogOpen(false)} disabled={exporting}>Cancel</Button>
              <Button
                onClick={handleExportModel}
                disabled={
                  exporting ||
                  lastCheckpointCount <= 0 ||
                  !stepText ||
                  Boolean(stepError)
                }
              >
                {exporting ? "Exporting" : "Export"}
              </Button>

            </DialogActions>
          </Dialog>


          <Dialog open={buildDetailsOpen} onClose={closeBuildDetails} maxWidth="md" fullWidth>
            <DialogTitle>
              Build Details{buildDetails?.modelName ? ` — ${buildDetails.modelName}` : ""}
            </DialogTitle>
            <DialogContent dividers>
              {!buildDetails ? null : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Key</strong></TableCell>
                        <TableCell><strong>Value</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(buildDetails)
                        .filter(([k, v]) => !dialogOmitKeys.has(k) && v !== undefined && v !== null && String(v) !== "")
                        .map(([k, v]) => (
                          <TableRow key={k}>
                            <TableCell>{buildLabelMap[k] || k}</TableCell>
                            <TableCell>{prettyVal(k, v)}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </DialogContent>
            <DialogActions>
              <Button size="small" onClick={closeBuildDetails}>Close</Button>
            </DialogActions>
          </Dialog>

          {/* === Available Models → Config Dialog (metrics table @ specific step) === */}
          <Dialog
            open={modelCfgOpen}
            onClose={() => setModelCfgOpen(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle sx={{ textAlign: "center" }}>
              {modelCfgName || "Model"} {modelCfgStepLabel ? `— ${modelCfgStepLabel}` : ""}
            </DialogTitle>

            {/* Show step specific model train and eval metrics */}
            <DialogContent dividers>
              {modelCfgLoading ? (
                <Box display="flex" justifyContent="center">
                  <CircularProgress />
                </Box>
              ) : modelCfgError ? (
                <Alert severity="warning">{modelCfgError}</Alert>
              ) : (
                <>
                  {/* TRAIN METRICS */}
                  {modelCfgNearestHint ? (
                    <Typography align="center" sx={{ mb: 2, color: "text.secondary" }}>
                      <em>{modelCfgNearestHint}</em>
                    </Typography>
                  ) : null}
                  <Typography align="center" sx={{ mb: 1, fontSize: "18px" }}>
                    Training Metrics
                  </Typography>
                  {modelCfgTrainRows.length ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Metric</strong></TableCell>
                            <TableCell><strong>Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modelCfgTrainRows.map((r, i) => (
                            <TableRow key={`tr-${i}`}>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{r.label}</TableCell>
                              <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {r.value}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                      No training metrics for this step.
                    </Typography>
                  )}

                  {/* EVAL METRICS */}
                  <Typography align="center" sx={{ mb: 1, fontSize: "18px" }}>
                    Evaluation Metrics
                  </Typography>
                  {modelCfgEvalRows.length ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Metric</strong></TableCell>
                            <TableCell><strong>Value</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modelCfgEvalRows.map((r, i) => (
                            <TableRow key={`ev-${i}`}>
                              <TableCell sx={{ whiteSpace: "nowrap" }}>{r.label}</TableCell>
                              <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                {r.value}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      No evaluation metrics for this step.
                    </Typography>
                  )}
                </>
              )}
            </DialogContent>

            <DialogActions>
              <Button size="small" onClick={() => setModelCfgOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>


        </Col>
      </Row>
    </div>
  );
};

export default ModelManagementForm;